/**
 * Set up tabs
 *
 * Sets up accessibility and behavior for tabs. Can work with either
 * one list of label toggles, or multiple (e.g. if goes from a tab
 * nav to an accordion at mobile).
 *
 * Example:

tabs({
	name: 'my-tabs', // slug to use for accessibility
	openFirstTab: true, // on load pre-expand first tab
	oneAtATime: true, // force one tab open at a time
	selectors: {
		wrap: '.my-tabs', // main wrapper containing all tab features
		toggles: ['.my-tabs__button--desk', '.my-tabs__button--mobile'], // string for one trigger or array for multiple
		content: '.tabs-item__content', // content area that gets expanded
	},
})

 *
 */

import debounce from 'lodash/debounce';

export const tabs = ({
	name,
	oneAtATime,
	openFirstTab,
	selectors: { wrap, toggles, content },
}) => {
	// for each tab group on the page
	document.querySelectorAll(wrap)?.forEach((tab, i) => {
		// set up content sections
		const id = `${name}-${i}`;
		const contentEls = tab.querySelectorAll(content);
		const tabCount = contentEls.length;

		// create a custom toggler array (from string or array of selectors)
		const togglesArr = (Array.isArray(toggles) ? toggles : [toggles]).map(
			(selector) => ({
				// identify this set of selectors (used for accessibility later)
				sluggish: selector.replace(/[^a-zA-Z ]/g, ''),
				// store all selector elements of this type
				el: tab.querySelectorAll(selector),
			})
		);

		// toggle tab expand/collapse
		const toggleTabs = (index) => {
			// for each set of toggles (though there may be just one)
			togglesArr.forEach((toggle) => {
				// loop through each toggle element
				toggle.el.forEach((el, j) => {
					// identify this tab: is it the requested tab and is it open
					const cur = j === index;
					const isOpen = el.getAttribute('aria-selected') === 'true';
					// if tab is requested and not open
					if (cur && !isOpen) {
						// open this tab
						el.setAttribute('aria-selected', true);
						contentEls[j].setAttribute('aria-hidden', false);
						contentEls[j].setAttribute('tabindex', 0);
					} else if (
						// if not the current tab and we should only have one open
						(!cur && oneAtATime) ||
						// if current tab and we needn't keep it open
						(cur && !oneAtATime)
					) {
						// close this tab
						el.setAttribute('aria-selected', false);
						contentEls[j].setAttribute('aria-hidden', true);
						contentEls[j].setAttribute('tabindex', -1);
					}
				});
			});
		};

		// loop through each content/toggle
		for (let j = 0; j < tabCount; j++) {
			// set up this particular iteration's ID
			const subId = `${id}-${j}`;

			// apply accessibility to content
			contentEls[j].setAttribute('id', subId);
			contentEls[j].setAttribute(
				'aria-hidden',
				openFirstTab ? !!j : true
			);
			contentEls[j].setAttribute('role', 'tabpanel');
			contentEls[j].setAttribute('tabindex', openFirstTab && !j ? 0 : -1);

			// set up the toggle(s) accessibility and behavior
			togglesArr.forEach((toggle, ij) => {
				const toggleId = `${toggle.sluggish}-${subId}`;
				toggle.el[j].setAttribute('id', toggleId);
				toggle.el[j].setAttribute('role', 'tab');
				toggle.el[j].setAttribute('aria-controls', `${subId}`);
				toggle.el[j].setAttribute(
					'aria-selected',
					openFirstTab ? !j : false
				);
				toggle.el[j].addEventListener('click', () => toggleTabs(j));
				// only set labelledby of content based on first controls found (index 0)
				if (!ij) {
					contentEls[j].setAttribute('aria-labelledby', toggleId);
				}
			});
		}

		// identify this as tab list with associrated tabs
		const toggleIdsArr = togglesArr.map((toggles) =>
			[...toggles.el].map((toggle) => toggle.id).join(' ')
		);

		if (toggleIdsArr.length) {
			tab.setAttribute('role', 'tablist');
			tab.setAttribute('aria-owns', toggleIdsArr.join(' '));
		}
	});
};

/**
 * Allow animation to height:auto
 *
 * Useful e.g. for accordions: this measures an element's
 * height, then adds a --h-auto variable to that element's
 * parent. That way, you can animate to --h-auto rather than
 * height:auto (which doesn't work). Applying height to the parent
 * rather than the element itself ensures padding also gets collapsed
 *
 * @param {string} selector Selector to measure (applies style to parent)
 */
export const setupAutoHeightAni = (selector) => {
	document.querySelectorAll(selector)?.forEach((el) => {
		el.parentElement.style.setProperty('--h-auto', `${el.offsetHeight}px`);
		window.addEventListener(
			'resize',
			debounce(() => {
				el.parentElement.style.setProperty(
					'--h-auto',
					`${el.offsetHeight}px`
				);
			}, 500)
		);
	});
};
